import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiClient from "../../services/apiClient";
import * as adminService from "../../services/adminService";
import { useAuth } from "../../services/AuthContext";

type CompanyAddress = {
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
};

type CompanyForm = {
  siret: string;
  legal_name: string;
  trade_name: string;
  base_currency: string;
  address_street: string;
  address_postal_code: string;
  address_city: string;
  address_country: string;
};

type OutletForm = {
  name: string;
  base_currency: string;
  address_street: string;
  address_postal_code: string;
  address_city: string;
  address_country: string;
};

type RegisterForm = {
  outlet_id: string;
  name: string;
};

type RegisterEditForm = {
  name: string;
  is_active: boolean;
};

const EMPTY_COMPANY_FORM: CompanyForm = {
  siret: "",
  legal_name: "",
  trade_name: "",
  base_currency: "EUR",
  address_street: "",
  address_postal_code: "",
  address_city: "",
  address_country: "",
};

const EMPTY_OUTLET_FORM: OutletForm = {
  name: "",
  base_currency: "EUR",
  address_street: "",
  address_postal_code: "",
  address_city: "",
  address_country: "",
};

const EMPTY_REGISTER_FORM: RegisterForm = {
  outlet_id: "",
  name: "",
};

const EMPTY_REGISTER_EDIT_FORM: RegisterEditForm = {
  name: "",
  is_active: true,
};

function toCompanyForm(company?: any): CompanyForm {
  const address: CompanyAddress =
    company && typeof company.address === "object" && company.address
      ? company.address
      : {};

  return {
    siret: company?.siret || "",
    legal_name: company?.legal_name || "",
    trade_name: company?.trade_name || "",
    base_currency: company?.base_currency || "EUR",
    address_street: address.street || "",
    address_postal_code: address.postal_code || "",
    address_city: address.city || "",
    address_country: address.country || "",
  };
}

const SuperadminEntityDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companyId = "" } = useParams<{ companyId: string }>();
  const { user } = useAuth() as any;

  const [activeView, setActiveView] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [company, setCompany] = useState<any | null>(null);
  const [formData, setFormData] = useState<CompanyForm>(EMPTY_COMPANY_FORM);

  const [outlets, setOutlets] = useState<any[]>([]);
  const [registers, setRegisters] = useState<any[]>([]);
  const [outletForm, setOutletForm] = useState<OutletForm>(EMPTY_OUTLET_FORM);
  const [registerForm, setRegisterForm] =
    useState<RegisterForm>(EMPTY_REGISTER_FORM);
  const [editingOutlet, setEditingOutlet] = useState<any | null>(null);
  const [editingOutletForm, setEditingOutletForm] =
    useState<OutletForm>(EMPTY_OUTLET_FORM);
  const [outletPendingDeletion, setOutletPendingDeletion] = useState<
    any | null
  >(null);
  const [editingRegister, setEditingRegister] = useState<any | null>(null);
  const [editingRegisterForm, setEditingRegisterForm] =
    useState<RegisterEditForm>(EMPTY_REGISTER_EDIT_FORM);

  const isSuperadmin = useMemo(
    () => Array.isArray(user?.roles) && user.roles.includes("superadmin"),
    [user?.roles],
  );

  const loadRelations = useCallback(async () => {
    if (!companyId) return;
    const [outletRows, registerRows] = await Promise.all([
      adminService.getCompanyOutlets(companyId),
      adminService.getCompanyRegisters(companyId),
    ]);
    setOutlets(outletRows || []);
    setRegisters(registerRows || []);
  }, [companyId]);

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    try {
      setError(null);
      setLoading(true);
      const response = await apiClient.get(`/api/companies/${companyId}`);
      const companyRow = response.company;
      setCompany(companyRow);
      setFormData(toCompanyForm(companyRow));
      setOutletForm((prev) => ({
        ...prev,
        base_currency: companyRow?.base_currency || "EUR",
      }));
      await loadRelations();
    } catch (e: any) {
      setError(e?.message || "Impossible de charger l'entité.");
    } finally {
      setLoading(false);
    }
  }, [companyId, loadRelations]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveCompany = async () => {
    if (!companyId) return;
    if (!formData.siret || !formData.legal_name || !formData.base_currency) {
      setError("SIRET, raison sociale et devise sont obligatoires.");
      return;
    }

    try {
      setError(null);
      await apiClient.put(`/api/companies/${companyId}`, {
        siret: formData.siret.trim(),
        legal_name: formData.legal_name.trim(),
        trade_name: formData.trade_name.trim() || null,
        base_currency: formData.base_currency.trim().toUpperCase(),
        address: {
          street: formData.address_street.trim() || null,
          postal_code: formData.address_postal_code.trim() || null,
          city: formData.address_city.trim() || null,
          country: formData.address_country.trim() || null,
        },
      });
      await loadAll();
    } catch (e: any) {
      setError(e?.message || "Impossible d'enregistrer l'entité.");
    }
  };

  const handleOpenOutletEditDialog = (outlet: any) => {
    const address = outlet?.address || {};
    setEditingOutlet(outlet);
    setEditingOutletForm({
      name: outlet?.name || "",
      base_currency: outlet?.base_currency || formData.base_currency || "EUR",
      address_street: address?.street || "",
      address_postal_code: address?.postal_code || "",
      address_city: address?.city || "",
      address_country: address?.country || "",
    });
  };

  const handleSaveOutlet = async () => {
    if (!editingOutlet?.id) return;
    if (!editingOutletForm.name.trim()) {
      setError(t("superadminEntityDetail.outlets.nameRequired"));
      return;
    }

    try {
      setError(null);
      await apiClient.put(`/api/outlets/${editingOutlet.id}`, {
        name: editingOutletForm.name.trim(),
        address: {
          street: editingOutletForm.address_street.trim() || null,
          postal_code: editingOutletForm.address_postal_code.trim() || null,
          city: editingOutletForm.address_city.trim() || null,
          country: editingOutletForm.address_country.trim() || null,
        },
      });
      setEditingOutlet(null);
      setEditingOutletForm(EMPTY_OUTLET_FORM);
      await loadRelations();
    } catch (e: any) {
      setError(e?.message || "Impossible de modifier l'outlet.");
    }
  };

  const handleDeleteOutlet = async () => {
    if (!outletPendingDeletion?.id) return;

    try {
      setError(null);
      await apiClient.delete(`/api/outlets/${outletPendingDeletion.id}`);
      await loadRelations();
      setOutletPendingDeletion(null);
    } catch (e: any) {
      setError(e?.message || "Impossible de supprimer l'outlet.");
    }
  };

  const handleOpenRegisterEditDialog = (register: any) => {
    setEditingRegister(register);
    setEditingRegisterForm({
      name: register?.name || "",
      is_active: Boolean(register?.is_active),
    });
  };

  const handleSaveRegister = async () => {
    if (!editingRegister?.id) return;
    if (!editingRegisterForm.name.trim()) {
      setError("Le nom du register est obligatoire.");
      return;
    }

    try {
      setError(null);
      await apiClient.put(`/api/registers/${editingRegister.id}`, {
        name: editingRegisterForm.name.trim(),
        is_active: editingRegisterForm.is_active,
      });
      setEditingRegister(null);
      setEditingRegisterForm(EMPTY_REGISTER_EDIT_FORM);
      await loadRelations();
    } catch (e: any) {
      setError(e?.message || "Impossible de modifier le register.");
    }
  };

  const handleDeleteRegister = async (register: any) => {
    if (!register?.id) return;
    const confirmed = window.confirm(
      t("superadminEntityDetail.registers.deleteConfirm", {
        name: register.name || "Sans nom",
      }) as string,
    );
    if (!confirmed) return;

    try {
      setError(null);
      await apiClient.delete(`/api/registers/${register.id}`);
      await loadRelations();
    } catch (e: any) {
      setError(e?.message || "Impossible de supprimer le register.");
    }
  };

  const handleCreateOutlet = async () => {
    if (!companyId) return;
    if (!outletForm.name.trim()) {
      setError(t("superadminEntityDetail.outlets.nameRequired"));
      return;
    }

    try {
      setError(null);
      await apiClient.post("/api/outlets", {
        company_id: companyId,
        name: outletForm.name.trim(),
        base_currency: outletForm.base_currency.trim().toUpperCase(),
        address: {
          street: outletForm.address_street.trim() || null,
          postal_code: outletForm.address_postal_code.trim() || null,
          city: outletForm.address_city.trim() || null,
          country: outletForm.address_country.trim() || null,
        },
      });
      setOutletForm({
        ...EMPTY_OUTLET_FORM,
        base_currency: formData.base_currency || "EUR",
      });
      await loadRelations();
    } catch (e: any) {
      setError(e?.message || "Impossible de créer l'outlet.");
    }
  };

  const handleCreateRegister = async () => {
    if (!companyId) return;
    if (!registerForm.outlet_id || !registerForm.name.trim()) {
      setError(t("superadminEntityDetail.registers.nameRequired"));
      return;
    }

    try {
      setError(null);
      await apiClient.post("/api/registers", {
        company_id: companyId,
        outlet_id: registerForm.outlet_id,
        name: registerForm.name.trim(),
        is_active: true,
      });
      setRegisterForm(EMPTY_REGISTER_FORM);
      await loadRelations();
    } catch (e: any) {
      setError(e?.message || "Impossible de créer le register.");
    }
  };

  if (!isSuperadmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Accès réservé au superadmin.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/superadmin/entities")}>
          {t("superadminEntityDetail.back")}
        </Button>
        <Typography variant="h4" fontWeight={700}>
          {company ? company.trade_name || company.legal_name : "Entité"}
        </Typography>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={activeView}
          onChange={(_, value: number) => setActiveView(value)}
          variant="scrollable">
          <Tab label={t("superadminEntityDetail.tabs.details")} />
          <Tab label={t("superadminEntityDetail.tabs.outlets")} />
          <Tab label={t("superadminEntityDetail.tabs.registers")} />
        </Tabs>
      </Paper>

      {loading ? <Typography>Chargement...</Typography> : null}

      {!loading && activeView === 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            }}>
            <TextField
              label={t("superadminEntityDetail.fields.siret")}
              value={formData.siret}
              onChange={(e) =>
                setFormData({ ...formData, siret: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.legalName")}
              value={formData.legal_name}
              onChange={(e) =>
                setFormData({ ...formData, legal_name: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.tradeName")}
              value={formData.trade_name}
              onChange={(e) =>
                setFormData({ ...formData, trade_name: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.currency")}
              value={formData.base_currency}
              onChange={(e) =>
                setFormData({ ...formData, base_currency: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.street")}
              value={formData.address_street}
              onChange={(e) =>
                setFormData({ ...formData, address_street: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.postalCode")}
              value={formData.address_postal_code}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  address_postal_code: e.target.value,
                })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.city")}
              value={formData.address_city}
              onChange={(e) =>
                setFormData({ ...formData, address_city: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.country")}
              value={formData.address_country}
              onChange={(e) =>
                setFormData({ ...formData, address_country: e.target.value })
              }
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSaveCompany}>
              {t("superadminEntityDetail.save")}
            </Button>
          </Box>
        </Paper>
      ) : null}

      {!loading && activeView === 1 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("superadminEntityDetail.outlets.tableName")}</TableCell>
                  <TableCell>{t("superadminEntityDetail.fields.currency")}</TableCell>
                  <TableCell>{t("superadminEntityDetail.fields.city")}</TableCell>
                  <TableCell align="right">{t("superadminEntityDetail.outlets.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outlets.map((outlet) => (
                  <TableRow key={outlet.id}>
                    <TableCell>{outlet.name}</TableCell>
                    <TableCell>{outlet.base_currency || "-"}</TableCell>
                    <TableCell>{outlet.address?.city || "-"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenOutletEditDialog(outlet)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setOutletPendingDeletion(outlet)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {outlets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>Aucun outlet.</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              mt: 2,
            }}>
            <TextField
              label={t("superadminEntityDetail.outlets.formName")}
              value={outletForm.name}
              onChange={(e) =>
                setOutletForm({ ...outletForm, name: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.currency")}
              value={outletForm.base_currency}
              onChange={(e) =>
                setOutletForm({ ...outletForm, base_currency: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.street")}
              value={outletForm.address_street}
              onChange={(e) =>
                setOutletForm({ ...outletForm, address_street: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.postalCode")}
              value={outletForm.address_postal_code}
              onChange={(e) =>
                setOutletForm({
                  ...outletForm,
                  address_postal_code: e.target.value,
                })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.city")}
              value={outletForm.address_city}
              onChange={(e) =>
                setOutletForm({ ...outletForm, address_city: e.target.value })
              }
            />
            <TextField
              label={t("superadminEntityDetail.fields.country")}
              value={outletForm.address_country}
              onChange={(e) =>
                setOutletForm({
                  ...outletForm,
                  address_country: e.target.value,
                })
              }
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleCreateOutlet}>
              {t("superadminEntityDetail.outlets.create")}
            </Button>
          </Box>
        </Paper>
      ) : null}

      {!loading && activeView === 2 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("superadminEntityDetail.registers.tableName")}</TableCell>
                  <TableCell>{t("superadminEntityDetail.registers.tableOutlet")}</TableCell>
                  <TableCell>{t("superadminEntityDetail.registers.tableActive")}</TableCell>
                  <TableCell align="right">{t("superadminEntityDetail.outlets.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registers.map((register) => (
                  <TableRow key={register.id}>
                    <TableCell>{register.name}</TableCell>
                    <TableCell>
                      {register.outlet_name || register.outlet_id}
                    </TableCell>
                    <TableCell>{register.is_active ? "Oui" : "Non"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenRegisterEditDialog(register)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteRegister(register)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {registers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>Aucun register.</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              mt: 2,
            }}>
            <TextField
              label={t("superadminEntityDetail.registers.tableOutlet")}
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              value={registerForm.outlet_id}
              onChange={(e) =>
                setRegisterForm({ ...registerForm, outlet_id: e.target.value })
              }>
              <option value="">{t("superadminEntityDetail.registers.selectPlaceholder")}</option>
              {outlets.map((outlet) => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </TextField>
            <TextField
              label={t("superadminEntityDetail.registers.formName")}
              value={registerForm.name}
              onChange={(e) =>
                setRegisterForm({ ...registerForm, name: e.target.value })
              }
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleCreateRegister}>
              {t("superadminEntityDetail.registers.create")}
            </Button>
          </Box>
        </Paper>
      ) : null}

      <Dialog
        open={Boolean(editingOutlet)}
        onClose={() => setEditingOutlet(null)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Modifier un outlet</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              mt: 1,
            }}>
            <TextField
              label="Nom outlet"
              value={editingOutletForm.name}
              onChange={(e) =>
                setEditingOutletForm({
                  ...editingOutletForm,
                  name: e.target.value,
                })
              }
            />
            <TextField
              label="Rue"
              value={editingOutletForm.address_street}
              onChange={(e) =>
                setEditingOutletForm({
                  ...editingOutletForm,
                  address_street: e.target.value,
                })
              }
            />
            <TextField
              label="Code postal"
              value={editingOutletForm.address_postal_code}
              onChange={(e) =>
                setEditingOutletForm({
                  ...editingOutletForm,
                  address_postal_code: e.target.value,
                })
              }
            />
            <TextField
              label="Ville"
              value={editingOutletForm.address_city}
              onChange={(e) =>
                setEditingOutletForm({
                  ...editingOutletForm,
                  address_city: e.target.value,
                })
              }
            />
            <TextField
              label="Pays"
              value={editingOutletForm.address_country}
              onChange={(e) =>
                setEditingOutletForm({
                  ...editingOutletForm,
                  address_country: e.target.value,
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingOutlet(null)}>Annuler</Button>
          <Button variant="contained" onClick={handleSaveOutlet}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(outletPendingDeletion)}
        onClose={() => setOutletPendingDeletion(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>{t("superadminEntityDetail.outletDelete.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("superadminEntityDetail.outletDelete.confirm", {
              name: outletPendingDeletion?.name || "Sans nom",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutletPendingDeletion(null)}>
            {t("superadminEntityDetail.outletDelete.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteOutlet}>
            {t("superadminEntityDetail.outletDelete.submit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingRegister)}
        onClose={() => setEditingRegister(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Modifier un register</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField
              label="Nom register"
              value={editingRegisterForm.name}
              onChange={(e) =>
                setEditingRegisterForm({
                  ...editingRegisterForm,
                  name: e.target.value,
                })
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography>Actif</Typography>
              <Switch
                checked={editingRegisterForm.is_active}
                onChange={(e) =>
                  setEditingRegisterForm({
                    ...editingRegisterForm,
                    is_active: e.target.checked,
                  })
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRegister(null)}>Annuler</Button>
          <Button variant="contained" onClick={handleSaveRegister}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SuperadminEntityDetailPage;
